import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { unwrap, type ApiError } from "@/api/errors";
import { queryKeys } from "@/api/queryKeys";
import type { FeedbackOut, FeedbackStatus } from "@/api/types";

export type { FeedbackOut } from "@/api/types";

export function useFeedback(orgSlug: string, projectSlug: string) {
  return useQuery<FeedbackOut[], ApiError>({
    queryKey: queryKeys.feedback(orgSlug, projectSlug),
    queryFn: async () =>
      unwrap(
        await api.GET("/orgs/{slug}/projects/{pslug}/feedback", { params: { path: { slug: orgSlug, pslug: projectSlug } } }),
      ),
  });
}

/** Staff only. Updates the cached list in place. */
export function useUpdateFeedbackStatus(orgSlug: string, projectSlug: string) {
  const qc = useQueryClient();
  const key = queryKeys.feedback(orgSlug, projectSlug);
  return useMutation<FeedbackOut, ApiError, { id: string; status: FeedbackStatus }, { prev?: FeedbackOut[] }>({
    mutationFn: async ({ id, status }) =>
      unwrap(
        await api.PATCH("/orgs/{slug}/projects/{pslug}/feedback/{feedback_id}", {
          params: { path: { slug: orgSlug, pslug: projectSlug, feedback_id: id } },
          body: { status },
        }),
      ),
    onMutate: ({ id, status }) => {
      const prev = qc.getQueryData<FeedbackOut[]>(key);
      qc.setQueryData<FeedbackOut[]>(key, (old) => old?.map((f) => (f.id === id ? { ...f, status } : f)));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
  });
}
